/**
 * IDDLAIDecisionAgent — Phase 6 · Agentic Responsible-AI-Use renderer.
 *
 * The agent has PROPOSED an autonomous action. The candidate must
 * weigh alternatives, retain human authority where required, and
 * decide approve / approve-with-sign-off / override / block.
 *
 * Scenario schema:
 *
 *   {
 *     task_brief: string,
 *     context: string,
 *     human_authority_required?: bool,
 *     agent_proposal: { action, count?, reasoning, reversibility, sign_off? },
 *     options: [{ id, label, details }]
 *   }
 *
 * Candidate-attributed events:
 *   decision_agent_workspace_opened  — mount
 *   agent_proposal_reviewed          — proposal panel opened
 *   option_considered                — { option_id }
 *   human_review_required            — click
 *   human_authority_retained         — explicit toggle
 *   conditional_ai_use_configured    — advisory click
 *   ai_use_declined                  — refusal click
 *   final_decision                   — { decision: <option_id> }
 */
import { useEffect, useRef, useState } from "react";
import { Gavel, Bot, ShieldAlert, UserCheck, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function IDDLAIDecisionAgent({
  attemptId, conceptId, scenarioText,
  environment = "agentic_ai",
  onEventLogged,
}) {
  const brief = scenarioText?.task_brief || "";
  const context = scenarioText?.context || "";
  const authorityRequired = !!scenarioText?.human_authority_required;
  const proposal = scenarioText?.agent_proposal || {};
  const options = scenarioText?.options || [];

  const [proposalSeen, setProposalSeen] = useState(false);
  const [considered, setConsidered] = useState(new Set());
  const [authorityRetained, setAuthorityRetained] = useState(false);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [selected, setSelected] = useState(null);
  const [finalized, setFinalized] = useState(null);
  const [busy, setBusy] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("decision_agent_workspace_opened", null, "decision_agent_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext, actor = "candidate") {
    try {
      const r = await api.post(`/attempts/${attemptId}/events`, {
        concept_id: conceptId,
        environment,
        event_name: eventName, event_value: eventValue,
        screen_context: screenContext, actor,
        client_ts_ms: Date.now(),
      }).then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} rejected: ${detail}`);
      return null;
    }
  }

  const reviewProposal = async () => {
    if (proposalSeen) return;
    setProposalSeen(true);
    await emit("agent_proposal_reviewed", null, "proposal");
  };

  const considerOption = async (opt) => {
    if (considered.has(opt.id)) return;
    await emit("option_considered", { option_id: opt.id }, "options");
    setConsidered((s) => new Set([...s, opt.id]));
    setSelected(opt.id);
  };

  const toggleAuthority = async () => {
    if (authorityRetained) return;
    setAuthorityRetained(true);
    await emit("human_authority_retained", null, "authority");
  };

  const toggleReview = async () => {
    if (reviewRequired) return;
    setReviewRequired(true);
    await emit("human_review_required", null, "authority");
  };

  const declineUse = async () => {
    await emit("ai_use_declined", null, "authority");
  };

  const configureConditional = async () => {
    await emit("conditional_ai_use_configured", { note: "sign_off_gate" }, "authority");
  };

  const finalize = async () => {
    if (!selected || busy || finalized) return;
    setBusy(true);
    try {
      await emit("final_decision", { decision: selected }, "finalize");
      setFinalized(selected);
      toast.success(`Recorded: ${selected}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="iddl-ai-decision-agent">
      {/* Left: proposal + governance */}
      <section className="border border-[var(--iddl-rule)] bg-white">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <Bot size={14} />
          <span className="font-display font-semibold">Agent proposal</span>
        </header>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-6 whitespace-pre-wrap" data-testid="decision-agent-brief">{brief}</p>
          {context ? (
            <p className="text-xs text-zinc-600 leading-5 whitespace-pre-wrap border-l-2 border-amber-400 pl-2">{context}</p>
          ) : null}

          <div className={`border ${authorityRequired ? "border-rose-400 bg-rose-50" : "border-[var(--iddl-rule)]"} p-2`}
            data-testid="decision-agent-proposal-panel"
            onClick={reviewProposal}>
            <div className="label-mono text-xs">Proposed autonomous action</div>
            <div className="text-sm font-semibold mt-1">{proposal.action || "—"}</div>
            {proposal.count != null ? <div className="text-xs">count: {proposal.count}</div> : null}
            {proposal.reasoning ? <div className="text-xs mt-1"><em>reasoning:</em> {proposal.reasoning}</div> : null}
            <div className="text-xs mt-1"><strong>reversibility:</strong> {proposal.reversibility || "—"}</div>
            <div className="text-xs"><strong>agent sign-off:</strong> {proposal.sign_off || <em>none</em>}</div>
            {authorityRequired && !authorityRetained && (
              <div className="text-xs text-rose-700 mt-2 flex items-center gap-1">
                <ShieldAlert size={12} /> This action requires HUMAN AUTHORITY before it can proceed.
              </div>
            )}
          </div>

          <div className="space-y-2 pt-2 border-t border-[var(--iddl-rule)]">
            <div className="label-mono text-xs">Governance</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={toggleAuthority} disabled={authorityRetained}
                className={`text-xs px-2 py-1 border ${authorityRetained ? "border-emerald-500 bg-emerald-50" : "border-[var(--iddl-rule)]"}`}
                data-testid="decision-agent-retain-authority">
                <UserCheck size={12} className="inline me-1" />
                {authorityRetained ? "Human authority retained" : "Retain human authority"}
              </button>
              <button type="button" onClick={toggleReview} disabled={reviewRequired}
                className={`text-xs px-2 py-1 border ${reviewRequired ? "border-emerald-500 bg-emerald-50" : "border-[var(--iddl-rule)]"}`}
                data-testid="decision-agent-require-review">
                <CheckCircle2 size={12} className="inline me-1" />
                {reviewRequired ? "Human review required" : "Require human review"}
              </button>
              <button type="button" onClick={configureConditional}
                className="text-xs px-2 py-1 border border-amber-400"
                data-testid="decision-agent-conditional">
                Add sign-off gate
              </button>
              <button type="button" onClick={declineUse}
                className="text-xs px-2 py-1 border border-rose-400"
                data-testid="decision-agent-decline">
                <Ban size={12} className="inline me-1" /> Decline AI use
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Right: options + finalize */}
      <section className="border border-[var(--iddl-rule)] bg-white" data-testid="decision-agent-options">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <Gavel size={14} />
          <span className="font-display font-semibold">Choose your decision</span>
          <span className="ms-auto label-mono text-xs">{considered.size}/{options.length} considered</span>
        </header>
        <div className="p-3 space-y-2">
          {options.map((opt) => {
            const isConsidered = considered.has(opt.id);
            const isSelected = selected === opt.id;
            return (
              <div key={opt.id}
                className={`border ${isSelected ? "border-emerald-500 bg-emerald-50/40" : "border-[var(--iddl-rule)]"} p-2 cursor-pointer`}
                onClick={() => considerOption(opt)}
                data-testid={`decision-agent-option-${opt.id}`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className="label-mono text-xs">{opt.id}</span>
                  {isConsidered && !isSelected ? <span className="label-mono text-xs text-zinc-500">considered</span> : null}
                  {isSelected ? <span className="label-mono text-xs text-emerald-800">chosen</span> : null}
                </div>
                <div className="text-sm font-semibold mt-1">{opt.label}</div>
                {opt.details ? <div className="text-xs mt-1 text-zinc-600">{opt.details}</div> : null}
              </div>
            );
          })}

          <div className="pt-2 border-t border-[var(--iddl-rule)]">
            <Button size="sm" variant="outline" className="w-full rounded-none"
              onClick={finalize} disabled={!selected || busy || !!finalized}
              data-testid="decision-agent-finalize">
              <CheckCircle2 size={14} className="me-1" /> Record final decision
            </Button>
            {finalized ? (
              <div className="text-xs text-emerald-800 pt-2">
                Recorded: <strong>{finalized}</strong>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
